import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Paper,
  Stack,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  InputAdornment,
  Tooltip,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { crmAPI, eventBatchAPI, trekAPI, adminAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import useListQueryParams from '../hooks/useListQueryParams';
import CrmLeadFormDialog from '../components/CrmLeadFormDialog';
import LeadRequestViewDialog from '../components/LeadRequestViewDialog.jsx';
import LeadRequestEditDialog from '../components/LeadRequestEditDialog.jsx';
import CreateSupportTicketDialog from '../components/CreateSupportTicketDialog.jsx';
import LeadRequestWhatsAppDialog from '../components/LeadRequestWhatsAppDialog.jsx';
import {
  REGISTRATION_LEAD_STATUS_LABELS,
  REGISTRATION_LEAD_STATUS_OPTIONS
} from '../constants/registrationLeadStatuses.js';
import { REGISTRATION_STATUS_OPTIONS } from '../constants/registrationStatuses.js';
import { sortEventBatchesByStartDateAsc } from '../utils/sortEventBatchesByStartDateDesc.js';
import { exportLeadsCsvForFilters } from '../utils/leadsExportCsv.js';
import { PageHeader, EmptyState, StatusChip } from '../components/ui';

const DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this_week', label: 'This Week' },
  { id: 'last_week', label: 'Last Week' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'last_3_months', label: 'Last 3 Months' },
  { id: 'last_6_months', label: 'Last 6 Months' },
  { id: 'fy_current', label: 'Current FY (2026-27)' },
  { id: 'fy_previous', label: 'Last FY (2025-26)' },
  { id: 'all_time', label: 'All Time' },
  { id: 'custom', label: 'Custom Range' }
];

const compactMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 280,
      '& .MuiMenuItem-root': {
        py: 0.5,
        minHeight: 30,
        fontSize: '12.5px'
      },
      '& .MuiCheckbox-root': {
        py: 0.25,
        px: 0.5
      },
      '& .MuiListItemText-primary': {
        fontSize: '12.5px'
      }
    }
  }
};

const getDateRangeForPreset = (preset) => {
  const now = new Date();
  const format = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  switch (preset) {
    case 'today':
      return { from: format(now), to: format(now) };
    case 'yesterday': {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      return { from: format(y), to: format(y) };
    }
    case 'this_week': {
      const d = new Date(now);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      d.setDate(diff);
      return { from: format(d), to: format(now) };
    }
    case 'last_week': {
      const d = new Date(now);
      const day = d.getDay();
      const diff = d.getDate() - day - 6 + (day === 0 ? -6 : 1);
      const start = new Date(d.setDate(diff));
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { from: format(start), to: format(end) };
    }
    case 'this_month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: format(start), to: format(now) };
    }
    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: format(start), to: format(end) };
    }
    case 'last_3_months': {
      const start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      return { from: format(start), to: format(now) };
    }
    case 'last_6_months': {
      const start = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      return { from: format(start), to: format(now) };
    }
    case 'fy_current': {
      const fyYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      const start = new Date(fyYear, 3, 1);
      return { from: format(start), to: format(now) };
    }
    case 'fy_previous': {
      const prevYear = now.getMonth() >= 3 ? now.getFullYear() - 1 : now.getFullYear() - 2;
      const start = new Date(prevYear, 3, 1);
      const end = new Date(prevYear + 1, 2, 31);
      return { from: format(start), to: format(end) };
    }
    case 'all_time':
      return { from: '', to: '' };
    default:
      return null;
  }
};

const ALL_LEAD_STATUS_OPTIONS = [
  { value: '(not set)', label: 'Not Set / New' },
  { value: 'converted', label: 'Converted' },
  { value: 'pending', label: 'Pending' },
  { value: 'ringing', label: 'Ringing' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'follow_up_done', label: 'Follow Up Done' },
  { value: 'second_follow_up_done', label: '2nd Follow Up Done' },
  { value: 'third_follow_up_done', label: '3rd Follow Up Done' },
  { value: 'call_back_required', label: 'Call Back Required' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'no_response', label: 'No Response' },
  { value: 'confirmed', label: 'Confirmed / Advance Paid' },
  { value: 'closed', label: 'Closed' }
];

const LEAD_SOURCE_OPTIONS = [
  { value: 'all', label: 'All Sources' },
  { value: 'pdf', label: 'Website Brochure (PDF)' },
  { value: 'crm', label: 'Direct CRM Leads' },
  { value: 'callback', label: 'Website Callbacks' },
  { value: 'registration', label: 'Direct Registrations' },
  { value: 'incoming_call', label: 'Incoming Calls' },
  { value: 'office_visit', label: 'Office Visits' }
];

const UPCOMING_REGISTRATION_TYPE = 'upcoming_registration';

const CRM_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'converted', label: 'Converted' },
  { value: 'pending', label: 'Pending' },
  { value: 'ringing', label: 'Ringing' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'closed', label: 'Closed' }
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'All categories' },
  { value: UPCOMING_REGISTRATION_TYPE, label: 'Upcoming batch registration' },
  { value: 'callback_request', label: 'Callback request' },
  { value: 'pdf_request', label: 'PDF request' },
  { value: 'incoming_call', label: 'Incoming call' },
  { value: 'office_visit', label: 'Office visit' }
];

const CATEGORY_LABEL = {
  [UPCOMING_REGISTRATION_TYPE]: 'Upcoming registration',
  callback_request: 'Callback',
  pdf_request: 'PDF',
  incoming_call: 'Incoming call',
  office_visit: 'Office visit'
};

function formatDate(createdAt) {
  if (!createdAt) return '—';
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatBatchDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime())
    ? '—'
    : x.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function registrationStatusLabel(value) {
  if (value == null || String(value).trim() === '') return '—';
  const v = String(value).trim().toLowerCase();
  const hit = REGISTRATION_STATUS_OPTIONS.find((o) => o.value === v);
  if (hit) return hit.label;
  return String(value).replace(/_/g, ' ');
}

function statusDisplayLabel(row) {
  const regStatus = String(row.registrationStatus || '').trim().toLowerCase();
  if (regStatus === 'confirmed' || regStatus === 'advanced_paid') {
    return 'Converted';
  }
  const key = String(row.leadStatus || '').trim();
  if (key === 'converted') return 'Converted';
  if (key) return REGISTRATION_LEAD_STATUS_LABELS[key] || key;
  return 'Not set';
}

function renderStatusMenuItems(statusOptions) {
  return statusOptions.flatMap((opt) => {
    if (opt.group && Array.isArray(opt.options)) {
      return [
        <ListSubheader key={`group-${opt.group}`}>{opt.group}</ListSubheader>,
        ...opt.options.map((sub) => (
          <MenuItem key={`${opt.group}-${sub.value}`} value={sub.value}>
            {sub.label}
          </MenuItem>
        ))
      ];
    }
    return (
      <MenuItem key={opt.value || 'all'} value={opt.value}>
        {opt.label}
      </MenuItem>
    );
  });
}

const filterGroupSx = { display: 'flex', alignItems: 'center', gap: 0.25 };

const LeadRequests = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { can } = useAuth();
  const canManage = can('lead_requests_manage');
  const canCreateLead = can('lead_requests_create');
  const canExportLeadsCsv = can('lead_requests_export');
  const [createOpen, setCreateOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [viewLead, setViewLead] = useState(null);
  const [editLead, setEditLead] = useState(null);
  const [whatsappLead, setWhatsappLead] = useState(null);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [pageInput, setPageInput] = useState('1');

  const clickToCallMutation = useMutation(
    (phone) => crmAPI.clickToCallLead({ phone }),
    {
      onSuccess: (res) => {
        showToast({
          message: res?.data?.message || 'Call initiated via Smartflo.',
          severity: 'success'
        });
      },
      onError: (err) =>
        showToast({
          message: err?.response?.data?.message || 'Could not initiate call.',
          severity: 'error'
        })
    }
  );

  const defaultRange = useMemo(() => getDateRangeForPreset('this_month') || { from: '', to: '' }, []);

  const { query, setQuery } = useListQueryParams({
    page: '1',
    limit: '20',
    datePreset: 'this_month',
    fromDate: defaultRange.from,
    toDate: defaultRange.to,
    source: 'all',
    leadType: '',
    status: '',
    statuses: '',
    search: '',
    events: '',
    adminUsers: '',
    sortOrder: 'desc',
    sortBy: 'createdAt',
    trekId: '',
    batchId: ''
  });

  const page = Number(query.page || 1);
  const limit = Number(query.limit || 20);
  const datePreset = query.datePreset || 'this_month';
  const fromDate = query.fromDate ?? defaultRange.from;
  const toDate = query.toDate ?? defaultRange.to;
  const sourceFilter = query.source || 'all';
  const leadTypeFilter = query.leadType || '';
  const search = query.search || '';
  const sortOrder = query.sortOrder || 'desc';
  const sortBy = query.sortBy === 'phone' ? 'phone' : 'createdAt';
  const trekFilter = query.trekId || '';
  const batchFilter = query.batchId || '';

  const statusFilter = useMemo(() => {
    const raw = query.statuses || query.status || '';
    if (!raw) return [];
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }, [query.statuses, query.status]);

  const selectedEvents = useMemo(() => {
    const raw = query.events || '';
    if (!raw) return [];
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }, [query.events]);

  const selectedAdminUsers = useMemo(() => {
    const raw = query.adminUsers || '';
    if (!raw) return [];
    return raw.split(',').map((s) => s.trim()).filter(Boolean).map(Number).filter((n) => Number.isFinite(n) && n > 0);
  }, [query.adminUsers]);

  const togglePhoneSort = () => {
    if (sortBy === 'phone') {
      setQuery({ sortOrder: sortOrder === 'asc' ? 'desc' : 'asc', page: '1' });
    } else {
      setQuery({ sortBy: 'phone', sortOrder: 'asc', page: '1' });
    }
  };

  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  const batchListParams = useMemo(
    () => ({
      sortBy: 'startDate',
      sortOrder: 'asc',
      noCount: 'true',
      upcomingOnly: 'true',
      limit: 500
    }),
    []
  );

  const { data: batchesData, isFetching: batchesLoading } = useQuery({
    queryKey: ['admin-event-batches-leads', trekFilter, batchListParams],
    queryFn: () => eventBatchAPI.list({ ...batchListParams, trekId: trekFilter }),
    enabled: Boolean(trekFilter)
  });
  const allBatches = batchesData?.data?.data || batchesData?.data || [];
  const batches = sortEventBatchesByStartDateAsc(
    trekFilter ? allBatches.filter((b) => String(b.trekId || b.trek?.id) === String(trekFilter)) : []
  );

  const { data: upcomingBatchesForTreksData } = useQuery({
    queryKey: ['admin-event-batches-for-leads-trek-filter'],
    queryFn: () => eventBatchAPI.list(batchListParams)
  });

  const { data: allTreksData } = useQuery({
    queryKey: ['admin-treks-for-leads'],
    queryFn: () => trekAPI.list({ page: 1, limit: 500 })
  });

  const { data: filterOptionsData } = useQuery(
    ['admin-report-filter-options'],
    async () => {
      const res = await adminAPI.getReportFilterOptions();
      return res.data?.data || {};
    },
    { staleTime: 10 * 60 * 1000 }
  );

  const { data: assigneesData } = useQuery(
    ['crm-assignees'],
    async () => {
      const res = await crmAPI.getAssignees();
      return res.data?.data || [];
    },
    { staleTime: 10 * 60 * 1000 }
  );
  const assignees = useMemo(() => assigneesData || [], [assigneesData]);

  const upcomingTreks = useMemo(() => {
    const rows = upcomingBatchesForTreksData?.data?.data || upcomingBatchesForTreksData?.data || [];
    const byId = new Map();
    for (const b of rows) {
      const t = b.trek;
      if (t?.id == null) continue;
      const key = String(t.id);
      if (!byId.has(key)) {
        byId.set(key, { id: t.id, name: t.name, slug: t.slug });
      }
    }
    return [...byId.values()].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [upcomingBatchesForTreksData]);

  const allTreks = allTreksData?.data?.data || [];
  const treks = useMemo(() => {
    const base = upcomingTreks.length ? upcomingTreks : allTreks;
    if (!trekFilter) return base;
    if (base.some((t) => String(t.id) === String(trekFilter))) return base;
    const extra = allTreks.find((t) => String(t.id) === String(trekFilter));
    return extra ? [...base, extra] : base;
  }, [upcomingTreks, allTreks, trekFilter]);

  const allAvailableEventNames = useMemo(() => {
    const set = new Set();
    const filterTreks = filterOptionsData?.treks || [];
    filterTreks.forEach((t) => t.name && set.add(t.name));
    allTreks.forEach((t) => t.name && set.add(t.name));
    treks.forEach((t) => t.name && set.add(t.name));
    set.add('General Inquiry / Unassigned');
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [filterOptionsData, allTreks, treks]);

  const trekIdNum = Number(trekFilter);
  const isValidTrekFilter = Boolean(
    trekFilter && Number.isFinite(trekIdNum) && trekIdNum > 0
  );
  const isValidBatchFilter = Boolean(
    batchFilter && Number.isFinite(Number(batchFilter)) && Number(batchFilter) > 0
  );

  const handlePresetChange = (newPreset) => {
    if (newPreset === 'all_time') {
      setQuery({ datePreset: 'all_time', fromDate: '', toDate: '', page: '1' });
      return;
    }
    if (newPreset !== 'custom') {
      const range = getDateRangeForPreset(newPreset);
      if (range) {
        setQuery({ datePreset: newPreset, fromDate: range.from, toDate: range.to, page: '1' });
        return;
      }
    }
    setQuery({ datePreset: newPreset, page: '1' });
  };

  const handleStatusFilterChange = (val) => {
    const arr = typeof val === 'string' ? val.split(',') : val;
    setQuery({ statuses: arr.join(','), status: '', page: '1' });
  };

  const handleClearFilters = () => {
    const range = getDateRangeForPreset('this_month') || { from: '', to: '' };
    setQuery({
      page: '1',
      datePreset: 'this_month',
      fromDate: range.from,
      toDate: range.to,
      source: 'all',
      leadType: '',
      status: '',
      statuses: '',
      search: '',
      events: '',
      adminUsers: '',
      trekId: '',
      batchId: '',
      sortOrder: 'desc',
      sortBy: 'createdAt'
    });
  };

  const isFiltered =
    datePreset !== 'this_month' ||
    Boolean(search.trim()) ||
    Boolean(leadTypeFilter) ||
    (sourceFilter && sourceFilter !== 'all') ||
    statusFilter.length > 0 ||
    selectedEvents.length > 0 ||
    selectedAdminUsers.length > 0 ||
    Boolean(trekFilter) ||
    Boolean(batchFilter);

  const buildFilterParams = () => {
    const params = {
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      source: sourceFilter !== 'all' ? sourceFilter : undefined,
      leadType: leadTypeFilter || undefined,
      statuses: statusFilter.length > 0 ? statusFilter.join(',') : undefined,
      events: selectedEvents.length > 0 ? selectedEvents.join(',') : undefined,
      adminUsers: selectedAdminUsers.length > 0 ? selectedAdminUsers.join(',') : undefined,
      search: search.trim() || undefined,
      sortOrder,
      sortBy
    };
    if (isValidTrekFilter && isValidBatchFilter) {
      params.trekId = trekFilter;
      params.batchId = batchFilter;
    } else if (isValidTrekFilter) {
      params.trekId = trekFilter;
    } else if (isValidBatchFilter) {
      params.batchId = batchFilter;
    }
    return params;
  };

  const params = {
    page,
    limit,
    ...buildFilterParams()
  };

  const { data, isLoading } = useQuery(
    [
      'lead-requests',
      datePreset,
      fromDate,
      toDate,
      sourceFilter,
      leadTypeFilter,
      statusFilter.join(','),
      selectedEvents.join(','),
      selectedAdminUsers.join(','),
      search,
      sortOrder,
      sortBy,
      trekFilter,
      batchFilter,
      page,
      limit
    ],
    () => crmAPI.listLeads(params)
  );

  const list = data?.data?.data || [];
  const pagination = data?.data?.pagination || { page: 1, totalPages: 1, total: 0 };
  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const total = Number(pagination.total) || 0;

  const goToPage = (nextPage) => {
    const n = Math.max(1, Math.min(totalPages, Number(nextPage) || 1));
    setQuery({ page: String(n) }, { resetPage: false });
  };

  const commitPageInput = () => {
    goToPage(pageInput);
    setPageInput(String(Math.max(1, Math.min(totalPages, Number(pageInput) || 1))));
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const result = await exportLeadsCsvForFilters(buildFilterParams());
      const rowPart =
        result.rowCount != null ? `${result.rowCount.toLocaleString('en-IN')} row(s)` : 'export';
      showToast({
        message: result.truncated
          ? `Downloaded ${rowPart} (export capped at 25,000 — narrow filters for full export).`
          : `Downloaded ${rowPart}.`,
        severity: 'success'
      });
    } catch (err) {
      showToast({
        message: err?.response?.data?.message || 'Could not download leads CSV.',
        severity: 'error'
      });
    } finally {
      setExportingCsv(false);
    }
  };

  const acceptCallMutation = useMutation((id) => crmAPI.acceptCall(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(['lead-requests']);
      queryClient.invalidateQueries(['crm-incoming-popups']);
      showToast({ message: 'Call accepted — complete the form.', severity: 'success' });
    },
    onError: (err) =>
      showToast({ message: err?.response?.data?.message || 'Could not accept call', severity: 'error' })
  });

  const [inquiriesAnchorEl, setInquiriesAnchorEl] = useState(null);
  const [selectedInquiriesRow, setSelectedInquiriesRow] = useState(null);
  const [syncConfirmLead, setSyncConfirmLead] = useState(null);

  const handleOpenInquiriesPopover = (event, row) => {
    event.stopPropagation();
    setInquiriesAnchorEl(event.currentTarget);
    setSelectedInquiriesRow(row);
  };

  const handleCloseInquiriesPopover = () => {
    setInquiriesAnchorEl(null);
    setSelectedInquiriesRow(null);
  };

  const openView = (row) => setViewLead(row);

  const openEdit = (row) => setEditLead(row);

  const updateStatusMutation = useMutation(
    ({ leadType, id, leadStatus, syncAllForPhone }) =>
      crmAPI.updateLead(leadType, id, { leadStatus: leadStatus || null, syncAllForPhone }),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['lead-requests']);
        const count = res?.data?.syncedCount;
        if (count && count > 0) {
          showToast({ message: `Status updated for all ${count + 1} inquiries.`, severity: 'success' });
        } else {
          showToast({ message: 'Lead updated.', severity: 'success' });
        }
      },
      onError: (err) =>
        showToast({
          message: err?.response?.data?.message || 'Failed to update',
          severity: 'error'
        })
    }
  );

  const handleStatusSelectChange = (row, newStatus) => {
    if (row.samePhoneCount > 1 && newStatus) {
      const opt = REGISTRATION_LEAD_STATUS_OPTIONS.find((o) => o.value === newStatus);
      setSyncConfirmLead({
        row,
        newStatus,
        newStatusLabel: opt?.label || newStatus
      });
    } else {
      updateStatusMutation.mutate({
        leadType: row.leadType,
        id: row.id,
        leadStatus: newStatus || null,
        syncAllForPhone: false
      });
    }
  };

  return (
    <div>
      <PageHeader
        title="Lead Requests"
        subtitle="PDF, callback, calls, office visits, and upcoming-batch registrations. Lead Status on registration rows does not change Registrations status."
        actions={
          <>
            {canExportLeadsCsv ? (
              <Button
                variant="outlined"
                startIcon={<DownloadRoundedIcon />}
                disabled={exportingCsv || total === 0}
                onClick={handleExportCsv}
              >
                {exportingCsv ? 'Downloading…' : 'Download Excel (CSV)'}
              </Button>
            ) : null}
            {canManage ? (
              <Button
                variant="outlined"
                startIcon={<SupportAgentRoundedIcon />}
                onClick={() => setTicketOpen(true)}
              >
                Create ticket
              </Button>
            ) : null}
            {canCreateLead ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                Create manual lead
              </Button>
            ) : null}
          </>
        }
      />

      {/* Rich Filter Options Card */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff'
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: '#334155',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '12px'
            }}
          >
            <FilterAltRoundedIcon sx={{ fontSize: 16, color: '#64748b' }} /> Filter Options
          </Typography>
          {isFiltered && (
            <Button
              size="small"
              onClick={handleClearFilters}
              sx={{ textTransform: 'none', fontSize: '12px', color: 'error.main', py: 0 }}
            >
              Reset All Filters
            </Button>
          )}
        </Stack>

        <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
          {/* 1. Date Preset Dropdown */}
          <FormControl size="small" sx={{ minWidth: 165 }}>
            <InputLabel sx={{ fontSize: '13px' }}>Date Preset</InputLabel>
            <Select
              value={datePreset}
              label="Date Preset"
              MenuProps={compactMenuProps}
              sx={{ fontSize: '13px' }}
              onChange={(e) => handlePresetChange(e.target.value)}
            >
              {DATE_PRESETS.map((p) => (
                <MenuItem key={p.id} value={p.id} sx={{ py: 0.5, fontSize: '12.5px' }}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 2. From Date */}
          <TextField
            type="date"
            size="small"
            label="From Date"
            value={fromDate}
            onChange={(e) => {
              setQuery({ fromDate: e.target.value, datePreset: 'custom', page: '1' });
            }}
            InputLabelProps={{ shrink: true, sx: { fontSize: '13px' } }}
            inputProps={{ sx: { fontSize: '13px' } }}
            sx={{ width: 145 }}
          />

          {/* 3. To Date */}
          <TextField
            type="date"
            size="small"
            label="To Date"
            value={toDate}
            onChange={(e) => {
              setQuery({ toDate: e.target.value, datePreset: 'custom', page: '1' });
            }}
            InputLabelProps={{ shrink: true, sx: { fontSize: '13px' } }}
            inputProps={{ sx: { fontSize: '13px' } }}
            sx={{ width: 145 }}
          />

          {/* 4. Multi-Select + Search for Events */}
          <Autocomplete
            multiple
            size="small"
            options={allAvailableEventNames}
            disableCloseOnSelect
            getOptionLabel={(option) => option}
            value={selectedEvents}
            onChange={(_, newValue) => {
              setQuery({ events: newValue.join(','), page: '1' });
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option} style={{ padding: '3px 8px', minHeight: 30, fontSize: '12.5px' }}>
                <Checkbox size="small" checked={selected} sx={{ p: 0.25, mr: 0.75 }} />
                <span style={{ fontSize: '12.5px' }}>{option}</span>
              </li>
            )}
            renderTags={(value, getTagProps) => {
              if (value.length === 0) return null;
              if (value.length <= 1) {
                return value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    sx={{ height: 22, fontSize: '11px', mr: 0.5 }}
                  />
                ));
              }
              return (
                <Chip
                  label={`${value.length} Events Selected`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '11px' }}
                />
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter Events (Multi)"
                placeholder={selectedEvents.length === 0 ? 'Search events...' : ''}
                size="small"
                InputLabelProps={{ sx: { fontSize: '13px' } }}
              />
            )}
            sx={{ minWidth: 240, maxWidth: 360, flexGrow: 1 }}
            ListboxProps={{ style: { maxHeight: 280, fontSize: '12.5px' } }}
          />

          {/* 5. Multi-Select + Search for Admin Users */}
          <Autocomplete
            multiple
            size="small"
            options={assignees}
            disableCloseOnSelect
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name || option.email || '')}
            isOptionEqualToValue={(option, value) => {
              const optId = typeof option === 'object' ? option.id : option;
              const valId = typeof value === 'object' ? value.id : value;
              return optId === valId;
            }}
            value={assignees.filter((a) => selectedAdminUsers.includes(a.id))}
            onChange={(_, newValue) => {
              const ids = newValue.map((item) => (typeof item === 'object' ? item.id : item));
              setQuery({ adminUsers: ids.join(','), page: '1' });
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id} style={{ padding: '3px 8px', minHeight: 30, fontSize: '12.5px' }}>
                <Checkbox size="small" checked={selected} sx={{ p: 0.25, mr: 0.75 }} />
                <div>
                  <span style={{ fontSize: '12.5px', fontWeight: 600 }}>{option.name}</span>
                  {option.role && (
                    <span style={{ fontSize: '11px', color: '#64748b', marginLeft: 6 }}>({option.role})</span>
                  )}
                </div>
              </li>
            )}
            renderTags={(value, getTagProps) => {
              if (value.length === 0) return null;
              if (value.length <= 1) {
                return value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                    sx={{ height: 22, fontSize: '11px', mr: 0.5 }}
                  />
                ));
              }
              return (
                <Chip
                  label={`${value.length} Admins Selected`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '11px' }}
                />
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Admin User / Counselor (Multi)"
                placeholder={selectedAdminUsers.length === 0 ? 'Search admin user...' : ''}
                size="small"
                InputLabelProps={{ sx: { fontSize: '13px' } }}
              />
            )}
            sx={{ minWidth: 230, maxWidth: 330, flexGrow: 1 }}
            ListboxProps={{ style: { maxHeight: 280, fontSize: '12.5px' } }}
          />

          {/* 6. Lead Source / Channel Filter */}
          <FormControl size="small" sx={{ minWidth: 165 }}>
            <InputLabel sx={{ fontSize: '13px' }}>Lead Source</InputLabel>
            <Select
              value={sourceFilter}
              label="Lead Source"
              MenuProps={compactMenuProps}
              sx={{ fontSize: '13px' }}
              onChange={(e) => {
                setQuery({ source: e.target.value, page: '1' });
              }}
            >
              {LEAD_SOURCE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ py: 0.5, fontSize: '12.5px' }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 7. Status Multi-Select Filter */}
          <FormControl size="small" sx={{ minWidth: 200, maxWidth: 300 }}>
            <InputLabel sx={{ fontSize: '13px' }}>Status (Multi)</InputLabel>
            <Select
              multiple
              value={statusFilter}
              input={<OutlinedInput label="Status (Multi)" />}
              MenuProps={compactMenuProps}
              sx={{ fontSize: '13px' }}
              renderValue={(selected) => {
                if (selected.length === 0) return 'All Statuses';
                return selected
                  .map((s) => ALL_LEAD_STATUS_OPTIONS.find((a) => a.value === s)?.label || s)
                  .join(', ');
              }}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            >
              {ALL_LEAD_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ py: 0.5, fontSize: '12.5px' }}>
                  <Checkbox checked={statusFilter.indexOf(opt.value) > -1} size="small" sx={{ py: 0.25, px: 0.5 }} />
                  <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: '12.5px' }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 8. Sort Dropdown */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ fontSize: '13px' }}>Sort By</InputLabel>
            <Select
              value={sortBy === 'phone' ? `phone-${sortOrder}` : sortOrder}
              label="Sort By"
              MenuProps={compactMenuProps}
              sx={{ fontSize: '13px' }}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'phone-asc' || v === 'phone-desc') {
                  setQuery({
                    sortBy: 'phone',
                    sortOrder: v === 'phone-asc' ? 'asc' : 'desc',
                    page: '1'
                  });
                } else {
                  setQuery({ sortBy: 'createdAt', sortOrder: v, page: '1' });
                }
              }}
            >
              <MenuItem value="desc" sx={{ py: 0.5, fontSize: '12.5px' }}>Latest first</MenuItem>
              <MenuItem value="asc" sx={{ py: 0.5, fontSize: '12.5px' }}>Oldest first</MenuItem>
              <MenuItem value="phone-asc" sx={{ py: 0.5, fontSize: '12.5px' }}>Phone A–Z</MenuItem>
              <MenuItem value="phone-desc" sx={{ py: 0.5, fontSize: '12.5px' }}>Phone Z–A</MenuItem>
            </Select>
          </FormControl>

          {/* 9. Optional Specific Trek Batch picker if trek is selected */}
          {trekFilter ? (
            <Box sx={filterGroupSx}>
              <Autocomplete
                size="small"
                sx={{ minWidth: 230 }}
                loading={batchesLoading}
                options={batches}
                getOptionLabel={(b) => (b ? `${formatBatchDate(b.startDate)} to ${formatBatchDate(b.endDate)}` : '')}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                value={batches.find((b) => String(b.id) === String(batchFilter)) ?? null}
                onChange={(_, batch) => setQuery({ batchId: batch ? String(batch.id) : '', page: '1' })}
                noOptionsText={batchesLoading ? 'Loading events…' : 'No events found'}
                ListboxProps={{ style: { maxHeight: 280, fontSize: '12.5px' } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Batch Date"
                    placeholder="Pick date batch…"
                    InputLabelProps={{ sx: { fontSize: '13px' } }}
                  />
                )}
              />
              {batchFilter ? (
                <IconButton size="small" aria-label="Clear event" onClick={() => setQuery({ batchId: '', page: '1' })}>
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
          ) : null}
        </Stack>

        {/* Dedicated Search Option to search any leads */}
        <Box sx={{ mt: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search any leads by name, phone number, email, reason, event, message..."
            value={search}
            onChange={(e) => setQuery({ search: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: '#64748b', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setQuery({ search: '' })}
                    edge="end"
                  >
                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { fontSize: '13.5px', backgroundColor: '#f8fafc', borderRadius: 1.5 }
            }}
          />
        </Box>

        {/* Selected Filter Chips Display */}
        {(selectedEvents.length > 0 || selectedAdminUsers.length > 0 || statusFilter.length > 0 || search.trim()) && (
          <Box sx={{ mt: 1.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pt: 1, borderTop: '1px dashed #f1f5f9' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Active Filters:
            </Typography>

            {search.trim() && (
              <Chip
                label={`Search: "${search.trim()}"`}
                size="small"
                icon={<SearchRoundedIcon sx={{ fontSize: '14px !important' }} />}
                onDelete={() => setQuery({ search: '' })}
                color="info"
                variant="outlined"
                sx={{ height: 22, fontSize: '11px' }}
              />
            )}

            {selectedEvents.map((ev) => (
              <Chip
                key={ev}
                label={ev}
                size="small"
                icon={<EventAvailableRoundedIcon sx={{ fontSize: '14px !important' }} />}
                onDelete={() => {
                  const next = selectedEvents.filter((item) => item !== ev);
                  setQuery({ events: next.join(','), page: '1' });
                }}
                color="primary"
                variant="outlined"
                sx={{ height: 22, fontSize: '11px' }}
              />
            ))}

            {selectedAdminUsers.map((uid) => {
              const u = assignees.find((a) => a.id === uid);
              return (
                <Chip
                  key={uid}
                  label={u ? u.name : `Admin #${uid}`}
                  size="small"
                  icon={<BadgeRoundedIcon sx={{ fontSize: '14px !important' }} />}
                  onDelete={() => {
                    const next = selectedAdminUsers.filter((item) => item !== uid);
                    setQuery({ adminUsers: next.join(','), page: '1' });
                  }}
                  color="secondary"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '11px' }}
                />
              );
            })}

            {statusFilter.map((s) => {
              const opt = ALL_LEAD_STATUS_OPTIONS.find((a) => a.value === s);
              return (
                <Chip
                  key={s}
                  label={opt ? opt.label : s}
                  size="small"
                  onDelete={() => {
                    const next = statusFilter.filter((item) => item !== s);
                    setQuery({ statuses: next.join(','), page: '1' });
                  }}
                  variant="outlined"
                  sx={{ height: 22, fontSize: '11px' }}
                />
              );
            })}
          </Box>
        )}
      </Paper>

      <Card>
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          {isLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : list.length === 0 ? (
            <EmptyState
              title="No requests found"
              description="Try changing category, status, trek, or date filters."
            />
          ) : (
            <TableContainer sx={{ overflowX: 'auto', mx: -0.5 }}>
              <Table size="small" sx={{ minWidth: 1180 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }} align="right">
                      <TableSortLabel
                        active={sortBy === 'phone'}
                        direction={sortBy === 'phone' ? sortOrder : 'asc'}
                        onClick={togglePhoneSort}
                        sx={{ flexDirection: 'row-reverse' }}
                      >
                        Phone
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Trek/Event</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Lead Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Registration status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Lead status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Assignee</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map((row) => {
                    const isUpcomingReg = row.leadType === UPCOMING_REGISTRATION_TYPE;
                    const isConfirmedOrAdvance = ['confirmed', 'advanced_paid'].includes(String(row.registrationStatus || '').toLowerCase());
                    const leadStatusValue = isConfirmedOrAdvance ? 'converted' : (row.leadStatus || '');

                    return (
                      <TableRow key={`${row.leadType}-${row.id}`} hover>
                        <TableCell>
                          <StatusChip
                            status={row.leadType}
                            label={CATEGORY_LABEL[row.leadType] || row.leadType}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{row.name || '—'}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, justifyContent: 'flex-end' }}>
                              <span>{row.phone || '—'}</span>
                              {canManage && row.phone ? (
                                <>
                                  <IconButton
                                    size="small"
                                    aria-label={`Call ${row.name || row.phone}`}
                                    onClick={() => clickToCallMutation.mutate(row.phone)}
                                    disabled={clickToCallMutation.isLoading}
                                    sx={{ color: 'info.main' }}
                                  >
                                    <PhoneInTalkIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    aria-label={`Send WhatsApp to ${row.name || row.phone}`}
                                    onClick={() => setWhatsappLead(row)}
                                    sx={{ color: '#25D366' }}
                                  >
                                    <WhatsAppIcon fontSize="small" />
                                  </IconButton>
                                </>
                              ) : null}
                            </Box>
                            {row.samePhoneCount > 1 ? (
                              <Tooltip title="Click to view all inquiries from this number">
                                <Chip
                                  size="small"
                                  label={`${row.samePhoneCount} inquiries`}
                                  onClick={(e) => handleOpenInquiriesPopover(e, row)}
                                  sx={{
                                    height: 20,
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    bgcolor: '#fef3c7',
                                    color: '#b45309',
                                    border: '1px solid #fde68a',
                                    '&:hover': { bgcolor: '#fde68a' }
                                  }}
                                />
                              </Tooltip>
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell>{row.eventLabel || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{formatDate(row.createdAt)}</TableCell>
                        <TableCell>
                          {isUpcomingReg ? (
                            <StatusChip
                              status={row.registrationStatus}
                              label={registrationStatusLabel(row.registrationStatus)}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          {canManage ? (
                            <Select
                              size="small"
                              displayEmpty
                              value={leadStatusValue}
                              onChange={(e) => handleStatusSelectChange(row, e.target.value)}
                              sx={{ minWidth: 170, bgcolor: 'background.paper' }}
                            >
                              <MenuItem value="">
                                <em>Not set</em>
                              </MenuItem>
                              {REGISTRATION_LEAD_STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              ))}
                            </Select>
                          ) : (
                            <StatusChip
                              status={leadStatusValue || 'default'}
                              label={statusDisplayLabel(row)}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {row.assignee?.name ? (
                            <Chip
                              size="small"
                              icon={<BadgeRoundedIcon sx={{ fontSize: '13px !important' }} />}
                              label={row.assignee.name}
                              variant="outlined"
                              sx={{
                                height: 24,
                                fontSize: '11.5px',
                                fontWeight: 500,
                                borderColor: 'primary.light',
                                color: 'primary.dark',
                                bgcolor: 'action.hover'
                              }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              gap: 0.5,
                              flexWrap: 'nowrap'
                            }}
                          >
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
                              onClick={() => openView(row)}
                              sx={{ minWidth: 0, px: 1 }}
                            >
                              View
                            </Button>
                            {canManage ? (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => openEdit(row)}
                                sx={{ minWidth: 0, px: 1 }}
                              >
                                Edit
                              </Button>
                            ) : null}
                            {row.leadType === 'incoming_call' &&
                            row.source === 'crm_leads' &&
                            (row.callStatus === 'ringing' || row.status === 'ringing') &&
                            can('lead_requests') ? (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => acceptCallMutation.mutate(row.id)}
                                disabled={acceptCallMutation.isLoading}
                                sx={{ minWidth: 0, px: 1.5 }}
                              >
                                Accept
                              </Button>
                            ) : null}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {total > 0 ? (
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap'
              }}
            >
              <Button size="small" variant="outlined" disabled={page <= 1} onClick={() => goToPage(1)}>
                First
              </Button>
              <Button size="small" variant="outlined" disabled={page <= 1} onClick={() => goToPage(page - 1)}>
                Previous
              </Button>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Typography variant="body2" color="text.secondary">
                  Page
                </Typography>
                <TextField
                  size="small"
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value.replace(/[^\d]/g, ''))}
                  onBlur={commitPageInput}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitPageInput();
                    }
                  }}
                  inputProps={{
                    inputMode: 'numeric',
                    style: { width: 48, textAlign: 'center', padding: '4px 6px' },
                    'aria-label': 'Page number'
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  of {totalPages} ({total.toLocaleString('en-IN')} total)
                </Typography>
              </Box>
              <Button size="small" variant="outlined" disabled={page >= totalPages} onClick={() => goToPage(page + 1)}>
                Next
              </Button>
              <Button size="small" variant="outlined" disabled={page >= totalPages} onClick={() => goToPage(totalPages)}>
                Last
              </Button>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      <LeadRequestViewDialog
        open={!!viewLead}
        lead={viewLead}
        onClose={() => setViewLead(null)}
      />

      <LeadRequestEditDialog
        open={!!editLead}
        lead={editLead}
        onClose={() => setEditLead(null)}
        onSaved={() => {
          queryClient.invalidateQueries(['lead-requests']);
          showToast({ message: 'Lead updated.', severity: 'success' });
        }}
      />

      <LeadRequestWhatsAppDialog
        open={!!whatsappLead}
        lead={whatsappLead}
        onClose={() => setWhatsappLead(null)}
        onSent={(result) =>
          showToast({
            message: result?.messageId
              ? `WhatsApp queued (ref ${String(result.messageId).slice(0, 24)}…). Check Waplify for delivery.`
              : 'WhatsApp template queued for delivery.',
            severity: 'success'
          })
        }
      />

      <CreateSupportTicketDialog
        open={ticketOpen}
        onClose={() => setTicketOpen(false)}
        onSaved={() => {
          showToast({ message: 'Support ticket created.', severity: 'success' });
        }}
      />

      <CrmLeadFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => {
          queryClient.invalidateQueries(['lead-requests']);
          showToast({ message: 'Lead created.', severity: 'success' });
        }}
        mode="create"
      />

      {/* Multiple Inquiries Popover */}
      <Popover
        open={Boolean(inquiriesAnchorEl)}
        anchorEl={inquiriesAnchorEl}
        onClose={handleCloseInquiriesPopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: { p: 2, width: 360, maxWidth: '95vw', borderRadius: 2, boxShadow: 4 }
        }}
      >
        {selectedInquiriesRow && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Inquiries for {selectedInquiriesRow.phone}
              </Typography>
              <Chip
                size="small"
                label={`${selectedInquiriesRow.samePhoneCount} total`}
                color="warning"
                sx={{ height: 20, fontSize: '11px', fontWeight: 600 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {selectedInquiriesRow.name && selectedInquiriesRow.name !== '—'
                ? selectedInquiriesRow.name
                : 'Customer'}{' '}
              has submitted {selectedInquiriesRow.samePhoneCount} inquiries:
            </Typography>
            <Stack spacing={1} sx={{ maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
              {(selectedInquiriesRow.samePhoneInquiries || [selectedInquiriesRow]).map((inq, idx) => (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    bgcolor: inq.id === selectedInquiriesRow.id ? 'action.selected' : 'background.paper',
                    borderColor: inq.id === selectedInquiriesRow.id ? 'primary.main' : 'divider'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12.5px', color: 'text.primary' }}>
                      {inq.eventLabel || inq.eventSlug || 'General Inquiry'}
                    </Typography>
                    <Chip
                      size="small"
                      label={CATEGORY_LABEL[inq.leadType] || inq.source || inq.leadType}
                      sx={{ height: 18, fontSize: '10px', textTransform: 'capitalize' }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
                      {formatDate(inq.createdAt)}
                    </Typography>
                    <Chip
                      size="small"
                      label={REGISTRATION_LEAD_STATUS_LABELS[inq.leadStatus] || inq.leadStatus || 'Not set'}
                      variant="outlined"
                      sx={{ height: 18, fontSize: '10px' }}
                    />
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </Popover>

      {/* Sync Status to All Same-Phone Inquiries Confirmation Dialog */}
      <Dialog
        open={Boolean(syncConfirmLead)}
        onClose={() => setSyncConfirmLead(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Multiple Inquiries Detected
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This phone number (<strong>{syncConfirmLead?.row?.phone}</strong>) has{' '}
            <strong>{syncConfirmLead?.row?.samePhoneCount} inquiries</strong> in total.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Do you want to update all {syncConfirmLead?.row?.samePhoneCount} inquiries from this customer to{' '}
            <strong>&quot;{syncConfirmLead?.newStatusLabel}&quot;</strong>, or only update this specific inquiry?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
          <Button onClick={() => setSyncConfirmLead(null)} color="inherit" size="small">
            Cancel
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const { row, newStatus } = syncConfirmLead;
              setSyncConfirmLead(null);
              updateStatusMutation.mutate({
                leadType: row.leadType,
                id: row.id,
                leadStatus: newStatus || null,
                syncAllForPhone: false
              });
            }}
          >
            Only This Inquiry
          </Button>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => {
              const { row, newStatus } = syncConfirmLead;
              setSyncConfirmLead(null);
              updateStatusMutation.mutate({
                leadType: row.leadType,
                id: row.id,
                leadStatus: newStatus || null,
                syncAllForPhone: true
              });
            }}
          >
            Update All {syncConfirmLead?.row?.samePhoneCount} Inquiries
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LeadRequests;
